// DataContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useFetch } from "use-http";
import { getDBURL, correctTimeZone } from "../Utils/Utils";
import { useErrorLogger } from '../Base components/Logger';
import { UserContext } from "../../App"; // 👈 get user from context

const DataContext = createContext();

export const DataProvider = ({ children }) => {
    const [fieldList, setFieldList] = useState([]);
    const [rootstockList, setRootstockList] = useState([]);
    const [currentSeason, setCurrentSeason] = useState(2000);
    const currentUser = useContext(UserContext);

    const { get, post } = useFetch(getDBURL());
    const { DBlogError } = useErrorLogger();

    useEffect(() => {

        async function loadData() {
            try {
                const timestamp = Date.now();
                const [ fieldsRes, rootstockRes, currentSeasonRes] = await Promise.all([                   
                    get(`/loadFields/dt=${timestamp}`),
                    get(`/loadRootstock`),
                    get(`loadCurrentSeasonYear/dt=${timestamp}`),
                ]);

                // Check if the response is successful
                if (!fieldsRes.success) {
                    throw new Error(`HTTP error! ${fieldsRes.message}`);
                }
                
                const fRes = fieldsRes.result;

                if (fRes !== undefined && fRes.length > 0) {
                    const itemlist = fRes[0].map((item) => ({
                        Id: item.Id,
                        RowFrom: item.RowFrom,
                        RowTo: item.RowTo,
                        FieldName: item.Name,
                        FieldType: item.Type,
                        VinesOnRow: item.VinesOnRow,
                        Description: item.Description,
                    }));
                    setFieldList(itemlist);
                }

                // Check if the response is successful
                if (!rootstockRes.success) {
                    throw new Error(`HTTP error! ${rootstockRes.message}`);
                }
             
                const rRes = rootstockRes.result;
                if (rRes !== undefined && rRes.length > 0) {
                    const rsl = rRes.map(item => ({
                        Id: item[0],
                        Name: item[1],
                    }))   
                    setRootstockList(rsl); 
                }

                // Check if the response is successful
                if (!currentSeasonRes.success) {
                    throw new Error(`HTTP error! ${currentSeasonRes.message}`);
                }
               
                const currentSn = currentSeasonRes.result;
                if (currentSn !== undefined) {
                    setCurrentSeason(currentSn);
                }

            } catch (error) {
                console.error('Error in loadData function:', error.message);

                await DBlogError({
                    error,
                    parameters: { },
                    userName: currentUser.email,
                    moduleName: 'DataProvider',
                    functionName: 'loadData',
                });
            }
        }
    
        loadData();
        
    }, []); // Fetch data on mount

    /*  The finishSeason function finalizes the current agricultural season 
        by marking all treatments and evaluations as completed and creating a new season. 
        It sends updates to the server, ensures the changes were successful, 
        and retrieves the updated current season year. 
        If all operations succeed, it updates the currentSeason state and returns 1. **/
    async function finishSeason() {
        try {
            const updateValues = {
                UserName: currentUser.email,
                Year: new Date().getFullYear(),
                FinishedAt: correctTimeZone(new Date()),
                FinishedBy: 'admin',
                LastSyncDate: correctTimeZone(new Date()),
            };

            const [finishTreatmentsResponse, finishEvaluationsResponse] = await Promise.all([
                post(`/finishAllTreatments/dt=${Date.now()}`, updateValues),
                post(`/finishAllEvaluationsAndAddNewSeason/dt=${Date.now()}`, updateValues),
            ]);

            if (
                finishTreatmentsResponse.success &&
                finishEvaluationsResponse.success &&
                finishTreatmentsResponse.result === 1 &&
                finishEvaluationsResponse.result === 1
            ) {
                const currentSeasonResponse = await get(`loadCurrentSeasonYear/dt=${Date.now()}`);
                if (currentSeasonResponse.success && currentSeasonResponse.result) {
                    setCurrentSeason(currentSeasonResponse.result);
                    return 1;
                }
            }
        } catch (error) {
            console.error('🔴 Error in finishSeason >>', error.message);

                await DBlogError({
                    error,
                    parameters: {},
                    userName: currentUser.email,
                    moduleName: 'DataProvider',
                    fufinishSeasonnctionName: 'loadData',
                });
            }
    };
        
    return (
        <DataContext.Provider 
            value={{ 
                fieldList, 
                rootstockList, 
                currentSeason, 
                finishSeason 
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export const useDataContext = () => useContext(DataContext);
